
import React from "react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface LoanTermSectionProps {
  loanTerm: number;
  setLoanTerm: (value: number) => void;
}

export const LoanTermSection = ({ loanTerm, setLoanTerm }: LoanTermSectionProps) => {
  // Calculate percentage for visual indicator
  const percentage = ((loanTerm - 1) / (25 - 1)) * 100;
  const isMobile = useIsMobile();
  
  // Calculate appropriate term label based on Finnish language rules
  const getTermLabel = (years: number) => {
    if (years === 1) return "1 vuosi";
    return `${years} vuotta`;
  };
  
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <Label htmlFor="loan-term" className="text-gray-700 font-medium">Laina-aika</Label>
        <motion.span 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          key={loanTerm}
          className="font-semibold text-blue-700 bg-blue-100 px-3 py-1 rounded-full text-sm shadow-sm"
        >
          {getTermLabel(loanTerm)}
        </motion.span>
      </div>
      
      <div className="relative p-4 bg-white rounded-lg shadow-sm border border-blue-50">
        <Slider 
          id="loan-term" 
          min={1} 
          max={25} 
          step={1} 
          value={[loanTerm]} 
          onValueChange={(value) => setLoanTerm(value[0])}
          className="my-4"
        />
        
        <div className={`flex justify-between ${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600 font-medium mt-2`}>
          <span className="bg-blue-50 px-2 py-1 rounded-full border border-blue-100/50">1 vuosi</span>
          <span className="bg-blue-50 px-2 py-1 rounded-full border border-blue-100/50">12 vuotta</span>
          <span className="bg-blue-50 px-2 py-1 rounded-full border border-blue-100/50">25 vuotta</span>
        </div>
        
        {/* Visual progress indicator line */}
        <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: `${percentage}%` }}></div>
      </div>
    </div>
  );
};
